/**
 * V3 item icon registry — TREATX-V3-ICON-CMS-01
 * =============================================
 * ONE curated catalogue shared by the public V3 homepage and the Super Admin
 * editor, so an icon the owner picks is exactly the icon the page renders.
 *
 * Why a curated registry and not the whole library: the icon name is persisted
 * CMS content. A fixed, reviewed list keeps the saved value meaningful, keeps
 * the admin grid searchable by words the owner actually uses, and keeps the
 * bundle to the icons we ship. `backend/services/landing_cms_service.py` holds
 * the matching name allow-list (`V3_ITEM_ICONS`) and a drift guard test asserts
 * the two never diverge.
 */
import {
  Activity, BadgeCheck, BadgePercent, BarChart3, BookOpen, Building2, Camera,
  CalendarDays, CheckCircle2, ClipboardCheck, ClipboardList, Clock, CreditCard,
  Download, Eye, FileCheck2, FileSearch, FileText, Globe2, GraduationCap,
  Handshake, Headphones, HeartPulse, Hotel, Image as ImageIcon, Languages, Lock,
  Mail, MapPin, MessageSquare, Microscope, Phone, Pill, Plane, Receipt, Search,
  Share2, ShieldCheck, Smartphone, Sparkles, Star, Stethoscope, Syringe,
  ThumbsUp, TrendingUp, UserRound, Users, Video, Wallet,
} from 'lucide-react';

export const V3_ICONS = [
  { name: 'Search', label: 'Search', category: 'Discovery', keywords: 'find lookup explore browse magnify', Icon: Search },
  { name: 'FileSearch', label: 'Compare details', category: 'Discovery', keywords: 'review compare inspect research', Icon: FileSearch },
  { name: 'MapPin', label: 'Location', category: 'Discovery', keywords: 'place city where map destination', Icon: MapPin },
  { name: 'Globe2', label: 'International', category: 'Discovery', keywords: 'world global country abroad', Icon: Globe2 },
  { name: 'Languages', label: 'Languages', category: 'Discovery', keywords: 'translate english turkish interpreter', Icon: Languages },
  { name: 'Eye', label: 'Transparency', category: 'Discovery', keywords: 'see view visible open clear', Icon: Eye },

  { name: 'Building2', label: 'Clinic or hospital', category: 'Providers', keywords: 'provider clinic hospital facility practice', Icon: Building2 },
  { name: 'Stethoscope', label: 'Doctor', category: 'Providers', keywords: 'physician medical consultation examination', Icon: Stethoscope },
  { name: 'UserRound', label: 'Patient', category: 'Providers', keywords: 'person profile account individual', Icon: UserRound },
  { name: 'Users', label: 'Team', category: 'Providers', keywords: 'staff people group colleagues', Icon: Users },
  { name: 'Microscope', label: 'Laboratory', category: 'Providers', keywords: 'lab diagnostics tests analysis', Icon: Microscope },
  { name: 'HeartPulse', label: 'Treatment care', category: 'Providers', keywords: 'health cardiology vitals wellbeing', Icon: HeartPulse },
  { name: 'Pill', label: 'Medication', category: 'Providers', keywords: 'drug prescription pharmacy tablet', Icon: Pill },
  { name: 'Syringe', label: 'Procedure', category: 'Providers', keywords: 'injection vaccine operation surgery', Icon: Syringe },
  { name: 'Activity', label: 'Recovery progress', category: 'Providers', keywords: 'monitor follow up vitals chart', Icon: Activity },
  { name: 'GraduationCap', label: 'Training', category: 'Providers', keywords: 'academy course education learning', Icon: GraduationCap },

  { name: 'ShieldCheck', label: 'Verified', category: 'Trust', keywords: 'verification security safe protected credential', Icon: ShieldCheck },
  { name: 'BadgeCheck', label: 'Accredited', category: 'Trust', keywords: 'certificate accreditation approved licence', Icon: BadgeCheck },
  { name: 'CheckCircle2', label: 'Confirmed', category: 'Trust', keywords: 'done complete tick success', Icon: CheckCircle2 },
  { name: 'Lock', label: 'Private', category: 'Trust', keywords: 'privacy secure confidential data', Icon: Lock },
  { name: 'FileCheck2', label: 'Checked document', category: 'Trust', keywords: 'validated credential review evidence', Icon: FileCheck2 },
  { name: 'Star', label: 'Quality', category: 'Trust', keywords: 'rating review favourite excellent', Icon: Star },
  { name: 'ThumbsUp', label: 'Recommended', category: 'Trust', keywords: 'approval satisfaction positive', Icon: ThumbsUp },
  { name: 'Handshake', label: 'Agreement', category: 'Trust', keywords: 'partnership deal contract trust', Icon: Handshake },

  { name: 'MessageSquare', label: 'Message', category: 'Communication', keywords: 'chat conversation enquiry ask contact', Icon: MessageSquare },
  { name: 'Mail', label: 'Email', category: 'Communication', keywords: 'inbox letter contact write', Icon: Mail },
  { name: 'Phone', label: 'Call', category: 'Communication', keywords: 'telephone ring voice contact', Icon: Phone },
  { name: 'Video', label: 'Video consultation', category: 'Communication', keywords: 'call online remote meeting', Icon: Video },
  { name: 'Headphones', label: 'Support', category: 'Communication', keywords: 'help assistance service desk', Icon: Headphones },
  { name: 'Share2', label: 'Share', category: 'Communication', keywords: 'send refer forward', Icon: Share2 },

  { name: 'FileText', label: 'Written quotation', category: 'Quotations', keywords: 'offer quote document itemised proposal', Icon: FileText },
  { name: 'ClipboardList', label: 'Treatment plan', category: 'Quotations', keywords: 'list plan itemised breakdown', Icon: ClipboardList },
  { name: 'ClipboardCheck', label: 'Approved plan', category: 'Quotations', keywords: 'accepted agreed confirmed checklist', Icon: ClipboardCheck },
  { name: 'Receipt', label: 'Itemised costs', category: 'Quotations', keywords: 'invoice bill price breakdown', Icon: Receipt },
  { name: 'CalendarDays', label: 'Appointment', category: 'Quotations', keywords: 'date schedule booking session', Icon: CalendarDays },
  { name: 'Clock', label: 'At your pace', category: 'Quotations', keywords: 'time duration wait schedule', Icon: Clock },

  { name: 'CreditCard', label: 'Card payment', category: 'Payments', keywords: 'pay checkout billing stripe', Icon: CreditCard },
  { name: 'Wallet', label: 'Payment', category: 'Payments', keywords: 'money balance budget pay', Icon: Wallet },
  { name: 'BadgePercent', label: 'Pricing offer', category: 'Payments', keywords: 'discount saving campaign percent', Icon: BadgePercent },
  { name: 'TrendingUp', label: 'Growth', category: 'Payments', keywords: 'increase revenue business chart', Icon: TrendingUp },
  { name: 'BarChart3', label: 'Reporting', category: 'Payments', keywords: 'statistics analytics figures data', Icon: BarChart3 },

  { name: 'Plane', label: 'Travel', category: 'Travel', keywords: 'flight abroad journey trip airport', Icon: Plane },
  { name: 'Hotel', label: 'Accommodation', category: 'Travel', keywords: 'stay hotel room lodging', Icon: Hotel },

  { name: 'ImageIcon', label: 'Photo', category: 'Media', keywords: 'picture image gallery visual', Icon: ImageIcon },
  { name: 'Camera', label: 'Before and after', category: 'Media', keywords: 'photo result gallery capture', Icon: Camera },
  { name: 'BookOpen', label: 'Guide', category: 'Media', keywords: 'read article information learn', Icon: BookOpen },
  { name: 'Sparkles', label: 'Highlight', category: 'Media', keywords: 'new special feature shine', Icon: Sparkles },
  { name: 'Smartphone', label: 'Mobile app', category: 'Media', keywords: 'phone app ios android device', Icon: Smartphone },
  { name: 'Download', label: 'Download', category: 'Media', keywords: 'install get save app', Icon: Download },
];

export const V3_ICON_CATEGORIES = [...new Set(V3_ICONS.map((i) => i.category))];

const BY_NAME = Object.fromEntries(V3_ICONS.map((i) => [i.name, i]));

export const DEFAULT_ITEM_ICON = 'CheckCircle2';

export const isKnownIcon = (name) => Boolean(name && BY_NAME[name]);

/**
 * Resolve a saved icon name to a component.
 * An unknown or obsolete identifier never breaks the page: the caller's
 * fallback is used, and failing that the neutral default.
 */
export const resolveIcon = (name, fallback = null) =>
  (BY_NAME[name]?.Icon) || fallback || BY_NAME[DEFAULT_ITEM_ICON].Icon;

export const iconMeta = (name) => BY_NAME[name] || null;

export const searchIcons = (query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return V3_ICONS;
  return V3_ICONS.filter((i) =>
    i.name.toLowerCase().includes(q)
    || i.label.toLowerCase().includes(q)
    || i.category.toLowerCase().includes(q)
    || i.keywords.includes(q));
};
